import { FormEvent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ThumbIcon } from '@itsrever/design-system'
import { useChatContext } from './chat-context'
import { useAssistantTracking } from './use-assistant-tracking'
import type { ChatFeedbackRating } from './types'

interface ChatFeedbackProps {
  // Index of the rated assistant message in the thread; the rating key.
  turnIndex: number
  question: string
  answer: string
  promptName?: string
}

const THUMB =
  'flex size-7 items-center justify-center rounded-lg text-white/50 outline-none transition-colors hover:bg-white/10 hover:text-white focus-visible:ring-2 focus-visible:ring-[#a2eeff] disabled:cursor-default data-[selected=true]:bg-white/10 data-[selected=true]:text-[#a2eeff] disabled:data-[selected=false]:opacity-30'

// Thumbs under one assistant reply, and — on a thumbs down — a box for what went
// wrong. The rating is recorded on click and the comment separately, so a user
// who rates and types nothing is still counted.
export function ChatFeedback({
  turnIndex,
  question,
  answer,
  promptName
}: ChatFeedbackProps) {
  const { t } = useTranslation()
  const { feedbackByIndex, setFeedbackByIndex } = useChatContext()
  const { trackFeedback, trackFeedbackComment } = useAssistantTracking()
  const [comment, setComment] = useState('')
  const [isCommentOpen, setCommentOpen] = useState(false)
  const [isCommentSent, setCommentSent] = useState(false)
  const commentRef = useRef<HTMLTextAreaElement>(null)

  const rating = feedbackByIndex.get(turnIndex)

  // The thread only scrolls itself when a message arrives, so a box opened under
  // the last reply lands below the fold and reads as nothing having happened.
  useEffect(() => {
    if (!isCommentOpen) return
    const box = commentRef.current
    box?.focus({ preventScroll: true })
    box?.scrollIntoView({ block: 'nearest' })
  }, [isCommentOpen])
  const exchange = { question, answer, promptName, turnIndex }

  // A rating is final: rating again would double-count the same reply.
  const rate = (next: ChatFeedbackRating) => {
    if (rating) return
    setFeedbackByIndex((prev) => new Map(prev).set(turnIndex, next))
    trackFeedback({ ...exchange, rating: next })
    // Only a negative rating asks why — the text is what makes it actionable.
    if (next === 'down') setCommentOpen(true)
  }

  const sendComment = (e: FormEvent) => {
    e.preventDefault()
    const text = comment.trim()
    if (!text) return
    trackFeedbackComment({ ...exchange, rating: 'down', comment: text })
    setCommentOpen(false)
    setCommentSent(true)
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => rate('up')}
          disabled={rating !== undefined}
          data-selected={rating === 'up'}
          aria-label={t('configuration_chat.feedback.helpful')}
          title={t('configuration_chat.feedback.helpful')}
          className={THUMB}
        >
          <ThumbIcon width={14} height={14} strokeWidth={1.8} />
        </button>
        <button
          type="button"
          onClick={() => rate('down')}
          disabled={rating !== undefined}
          data-selected={rating === 'down'}
          aria-label={t('configuration_chat.feedback.not_helpful')}
          title={t('configuration_chat.feedback.not_helpful')}
          className={THUMB}
        >
          <ThumbIcon
            direction="down"
            width={14}
            height={14}
            strokeWidth={1.8}
          />
        </button>
        {isCommentSent && (
          <span className="ml-1 text-[11px] text-white/50">
            {t('configuration_chat.feedback.thanks')}
          </span>
        )}
      </div>

      {isCommentOpen && (
        <form
          onSubmit={sendComment}
          className="flex flex-col gap-2 rounded-xl border border-white/10 bg-white/5 p-3"
        >
          <label
            htmlFor={`chat-feedback-${turnIndex}`}
            className="text-[11px] font-medium text-white/70"
          >
            {t('configuration_chat.feedback.comment_label')}
          </label>
          <textarea
            ref={commentRef}
            id={`chat-feedback-${turnIndex}`}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            placeholder={t('configuration_chat.feedback.comment_placeholder')}
            className="resize-none rounded-lg border border-white/10 bg-[#020c1c] p-2 text-xs text-white outline-none placeholder:text-white/30 focus:border-[#a2eeff]"
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setCommentOpen(false)}
              className="rounded-lg px-2.5 py-1.5 text-[11px] font-medium text-white/60 outline-none transition-colors hover:bg-white/10 hover:text-white focus-visible:ring-2 focus-visible:ring-[#a2eeff]"
            >
              {t('configuration_chat.feedback.skip')}
            </button>
            <button
              type="submit"
              disabled={!comment.trim()}
              className="rounded-lg bg-gradient-to-br from-[#1790ff] to-[#1b75eb] px-2.5 py-1.5 text-[11px] font-semibold text-white outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-[#a2eeff] disabled:opacity-40"
            >
              {t('configuration_chat.feedback.send')}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
